import { useCallback, useState } from 'react';

type StepWithTracking = {
  id: string;
  'data-track-container'?: string;
} & Record<string, unknown>;

/**
 * Hook to track the current step's data-track-container value
 * Works with MultistepForm's onStepChange callback to provide tracking data
 * @param steps - Array of step objects that may contain data-track-container
 * @returns Object with currentTrackContainer and onStepChange function
 */
function useStepTracking(steps: StepWithTracking[]) {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const currentTrackContainer = steps[currentStepIndex]?.['data-track-container'];

  const onStepChange = useCallback(
    (isFirstStep: boolean, isLastStep: boolean, stepId?: string) => {
      if (stepId) {
        const index = steps.findIndex(step => step.id === stepId);
        if (index >= 0) {
          setCurrentStepIndex(index);
        }
        return;
      }

      if (isFirstStep) {
        setCurrentStepIndex(0);
      } else if (isLastStep) {
        setCurrentStepIndex(steps.length - 1);
      } else {
        // For middle steps, we increment from current position
        // This is a limitation but works for most common flows
        setCurrentStepIndex(prev => {
          if (prev === 0) return 1; // Moving forward from first
          if (prev === steps.length - 1) return steps.length - 2; // Moving back from last
          return prev; // Stay at current for ambiguous cases
        });
      }
    },
    [steps]
  );

  return {
    currentTrackContainer,
    onStepChange,
    currentStepIndex,
  };
}

export { useStepTracking, type StepWithTracking };
